import { trigger, state, style, transition, animate } from '@angular/animations';
import { Component, computed, effect, inject, input, output, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import {
  faChevronDown,
  faChevronUp,
  faClone,
  faCodeBranch,
  faCommentPlus,
  faEye,
  faGear,
  faPhotoFilm,
  faTrashCan,
  faBookmark
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { Subscription } from 'rxjs';

import { Media } from '@eiq/files/models';

import { FileType } from '@empathy/global/data';
import { QuestionType } from '@empathy/survey/base';
import { MediaForm } from '@empathy/survey-core';

import { QuestionTypeChangeOptions } from '../../models/question-type-change.constants';
import { QuestionTypeConfig } from '../../models/question-type-config.constants';
import { QuestionTypeFormComponent } from '../../models/question-type-form-component.constants';
import { SideTabs } from '../../models/side-tabs.enum';
import { SurveyEditorService } from '../../services/survey-editor.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ChangeQuestionTypeDialogComponent } from '../dialogs/change-question-type-dialog/change-question-type-dialog.component';
import { StimulusPickerDialogComponent } from '../stimulus/stimulus-picker/stimulus-picker-dialog.component';

@Component({
  selector: 'empathy-question-form',
  templateUrl: 'question-form.component.html',
  styleUrls: ['./question-form.component.scss'],
  animations: [
    trigger('grow', [
      state(
        'true',
        style({
          padding: '16px 32px 16px 32px',
          height: '*'
        })
      ),
      state(
        'false',
        style({
          padding: '0 32px 0 32px',
          height: '40px'
        })
      ),
      transition('true => false', animate('400ms ease-in-out')),
      transition('false => true', animate('400ms ease-in-out'))
    ])
  ]
})
export class QuestionFormComponent {
  @ViewChild('tooltip') tooltip: MatTooltip;

  activeQuestion = input<FormGroup>();

  duplicateQuestion = output();
  remove = output();
  saveQuestionOnLibrary = output();
  saveOptionsOnLibrary = output();
  updateCommentsReference = output();

  readonly surveyEditorService = inject(SurveyEditorService);

  isEndQuestionType$ = computed(() => this.activeQuestion()?.get('type').value === QuestionType.Terminate);
  isInfoQuestionType$ = computed(() => this.activeQuestion()?.get('type').value === QuestionType.InfoPage);
  isHiddenQuestionType$ = computed(() => this.activeQuestion()?.get('type').value === QuestionType.HiddenChoice);
  allowMultipleStimulus$ = computed(() => this.activeQuestion()?.get('type').value !== QuestionType.AIChat);
  typeChangeOptions$ = computed(() => QuestionTypeChangeOptions[this.activeQuestion()?.get('type').value] ?? []);

  questionType = QuestionType;
  questionTypeFormComponent = QuestionTypeFormComponent;
  questionTypeConfig = QuestionTypeConfig;
  sideTabs = SideTabs;

  faBookmark = faBookmark;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faCodeBranch = faCodeBranch;
  faCommentPlus = faCommentPlus;
  faClone = faClone;
  faEllipsisVertical = faEllipsisVertical;
  faEye = faEye;
  faGear = faGear;
  faPhotoFilm = faPhotoFilm;
  faTrashCan = faTrashCan;

  private currentCode: string;

  private dialog = inject(MatDialog);

  private subscription = new Subscription();

  constructor() {
    effect(() => {
      this.currentCode = this.activeQuestion()?.value.code;
    });

    effect(
      (onCleanup: Function) => {
        if (this.subscription) {
          this.subscription.unsubscribe();
        }

        const control = this.activeQuestion();
        if (control) {
          this.isCodeDuplicated(control.value.code);
          this.subscription = control.get('code').valueChanges.subscribe((val) => {
            this.isCodeDuplicated(val);
          });
        }

        onCleanup(() => {
          this.subscription.unsubscribe();
        });
      },
      {
        allowSignalWrites: true
      }
    );
  }

  onCheckDuplicate(): void {
    if (this.activeQuestion().get('code').value.length === 0 || this.activeQuestion().get('code').hasError('duplicated')) {
      this.activeQuestion().get('code').setValue(this.currentCode);
    } else {
      this.currentCode = this.activeQuestion().get('code').value;
      this.updateCommentsReference.emit();
    }
  }

  onChangeQuestionType(targetType: QuestionType): void {
    const { affectedQuestions, affectedBlocks } = this.surveyEditorService.conditionsAffectedByTypeChange(targetType);

    if (affectedQuestions.length === 0 && affectedBlocks.length === 0) {
      this.surveyEditorService.changeQuestionType(targetType);
      return;
    }

    this.dialog
      .open(ChangeQuestionTypeDialogComponent, {
        disableClose: true,
        data: {
          questionCode: this.activeQuestion().get('code').value,
          targetTypeText: this.questionTypeConfig[targetType].text,
          affectedQuestions,
          affectedBlocks
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.surveyEditorService.changeQuestionType(targetType);
        }
      });
  }

  onOpenSideTab(sideTab: SideTabs): void {
    this.resetActiveElements();
    this.surveyEditorService.activeTab$.set(sideTab);
  }

  onAddStimulus(): void {
    const dialogRef = this.dialog.open(StimulusPickerDialogComponent, {
      data: {
        types: [FileType.ImageJpeg, FileType.ImagePng, FileType.ImageGif, FileType.VideoMp4],
        multipleInd: this.allowMultipleStimulus$()
      }
    });

    dialogRef.afterClosed().subscribe((files: Media[]) => {
      if (files) {
        files.map((media) => new MediaForm(media).form).forEach((mediaForm) => (this.activeQuestion().get('stimulus') as FormArray).push(mediaForm));
      }
    });
  }

  onDeleteMedia(optionIndex: number): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Stimulus',
        confirmText: 'Are you sure you want to remove this stimulus?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        (this.activeQuestion().get('stimulus') as FormArray).removeAt(optionIndex);
      }
    });
  }

  private resetActiveElements(): void {
    this.surveyEditorService.surveyEditor$().activeOption = null;
    this.surveyEditorService.surveyEditor$().activeCard = null;
  }

  private isCodeDuplicated(qCode: any): void {
    const questionsToCheck = [...this.surveyEditorService.surveyEditor$().questionsHidden, ...this.surveyEditorService.surveyEditor$().questions];

    const isDuplicated =
      questionsToCheck.filter((q) => q.code.toLowerCase() === qCode.toLowerCase() && q.id !== this.activeQuestion().get('id').value).length > 0;

    this.activeQuestion()
      .get('code')
      .setErrors(isDuplicated ? { duplicated: true } : null);

    if (isDuplicated) {
      setTimeout(() => {
        this.tooltip?.show();
      });
    }
  }
}
